/**
 * SearchableSelect — a dependency-free, keyboard-accessible combobox for the admin
 * console. Supports single-select and multi-select modes and matches the existing admin
 * visual language: the trigger reuses the `rounded-lg border border-input bg-background`
 * input token, and selected items in multi-mode render as the same removable pills used
 * in the users table (`rounded-full bg-secondary border border-border`).
 *
 * Why custom: the repo has no dropdown library and native <select> can't be made
 * searchable. This keeps a11y (roles, aria-activedescendant, full keyboard nav, visible
 * focus ring) without pulling in react-select/radix.
 *
 * Keyboard: type to filter; ↑/↓ move the active option; Enter selects/toggles;
 * Escape closes; Backspace on an empty query removes the last selected chip (multi).
 */
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, X } from "lucide-react";

export interface SelectOption {
  value: string;
  label: string;
}

interface CommonProps {
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  ariaLabel: string;
  /** Allow free-typed values not in `options` (committed on Enter). Default false. */
  allowCustom?: boolean;
  /** Compact trigger (used inline in a dense table cell). */
  compact?: boolean;
}

interface SingleProps extends CommonProps {
  multiple?: false;
  value: string | null;
  onChange: (value: string | null) => void;
}

interface MultiProps extends CommonProps {
  multiple: true;
  value: string[];
  onChange: (value: string[]) => void;
}

type Props = SingleProps | MultiProps;

function labelFor(options: SelectOption[], value: string): string {
  return options.find((o) => o.value === value)?.label ?? value;
}

export default function SearchableSelect(props: Props) {
  const {
    options,
    placeholder = "Select…",
    disabled = false,
    ariaLabel,
    allowCustom = false,
    compact = false,
  } = props;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const openRef = useRef(open);
  openRef.current = open;
  const listboxId = useId();

  // The menu is portaled to <body> with position:fixed so no ancestor's
  // overflow (e.g. the users table's overflow-x-auto wrapper) can clip it and
  // no ancestor stacking context can bury it. We track the trigger's viewport
  // rect and mirror it onto the fixed menu.
  const [menuRect, setMenuRect] = useState<{
    left: number;
    top: number;
    width: number;
  } | null>(null);

  const updateMenuRect = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setMenuRect({ left: r.left, top: r.bottom + 4, width: r.width });
  }, []);

  // Measure synchronously before paint when opening, and keep the menu pinned
  // to the trigger while scrolling/resizing the page underneath it.
  useLayoutEffect(() => {
    if (!open) return;
    updateMenuRect();
    window.addEventListener("scroll", updateMenuRect, true); // capture: catch scrolls in any ancestor
    window.addEventListener("resize", updateMenuRect);
    return () => {
      window.removeEventListener("scroll", updateMenuRect, true);
      window.removeEventListener("resize", updateMenuRect);
    };
  }, [open, updateMenuRect]);

  const isMulti = props.multiple === true;
  const selectedValues = isMulti
    ? props.value
    : props.value != null
      ? [props.value]
      : [];

  // Options matching the query, minus those already selected (multi only).
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return options.filter((o) => {
      if (isMulti && selectedValues.includes(o.value)) return false;
      if (!q) return true;
      return (
        o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q)
      );
    });
  }, [options, query, isMulti, selectedValues]);

  // A custom-value row appears when allowCustom and the query is a novel token.
  const customCandidate =
    allowCustom &&
    query.trim().length > 0 &&
    !options.some((o) => o.value.toLowerCase() === query.trim().toLowerCase()) &&
    !selectedValues.includes(query.trim())
      ? query.trim()
      : null;

  const rows: Array<{ value: string; label: string; isCustom: boolean }> = [
    ...filtered.map((o) => ({ value: o.value, label: o.label, isCustom: false })),
    ...(customCandidate
      ? [{ value: customCandidate, label: `Add "${customCandidate}"`, isCustom: true }]
      : []),
  ];

  // Keep the active row in range as the filtered set changes.
  useEffect(() => {
    setActiveIndex((i) => Math.min(i, Math.max(0, rows.length - 1)));
  }, [rows.length]);

  // Close on outside click. Attach the listener ONCE (deps []) and read the live `open`
  // via a ref inside the handler — so unrelated re-renders while open don't churn
  // add/removeEventListener and can't accumulate duplicate listeners.
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!openRef.current) return;
      const target = e.target as Node;
      // The menu is portaled outside rootRef, so check it separately.
      if (
        rootRef.current &&
        !rootRef.current.contains(target) &&
        (!menuRef.current || !menuRef.current.contains(target))
      ) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const commit = (value: string) => {
    if (isMulti) {
      if (!selectedValues.includes(value)) {
        (props as MultiProps).onChange([...selectedValues, value]);
      }
      setQuery("");
      // keep open for multi so the admin can pick several in a row
      inputRef.current?.focus();
    } else {
      (props as SingleProps).onChange(value);
      setQuery("");
      setOpen(false);
    }
  };

  const removeValue = (value: string) => {
    if (isMulti) {
      (props as MultiProps).onChange(selectedValues.filter((v) => v !== value));
    } else {
      (props as SingleProps).onChange(null);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!open) setOpen(true);
        setActiveIndex((i) => Math.min(i + 1, rows.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        break;
      case "Enter": {
        e.preventDefault();
        const row = rows[activeIndex];
        if (row) commit(row.value);
        break;
      }
      case "Escape":
        e.preventDefault();
        setOpen(false);
        setQuery("");
        break;
      case "Backspace":
        if (query === "" && isMulti && selectedValues.length > 0) {
          removeValue(selectedValues[selectedValues.length - 1]);
        }
        break;
      default:
        break;
    }
  };

  const showQueryInline = open || (isMulti && selectedValues.length === 0 && !open);

  return (
    <div ref={rootRef} className="relative">
      {/* Trigger / control */}
      <div
        className={
          "flex flex-wrap items-center gap-1 rounded-lg border border-input bg-background " +
          "text-sm focus-within:ring-2 focus-within:ring-ring " +
          (disabled ? "opacity-50 cursor-not-allowed " : "cursor-text ") +
          (compact ? "px-1.5 py-1 min-h-[32px] " : "px-2 py-1.5 min-h-[38px] ")
        }
        onClick={() => {
          if (disabled) return;
          setOpen(true);
          inputRef.current?.focus();
        }}
      >
        {/* Selected chips (multi) */}
        {isMulti &&
          selectedValues.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 rounded-full bg-secondary border border-border px-2 py-0.5 text-xs"
            >
              {labelFor(options, v)}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeValue(v);
                }}
                aria-label={`Remove ${labelFor(options, v)}`}
                className="hover:text-destructive transition-colors"
              >
                <X className="w-3 h-3" aria-hidden="true" />
              </button>
            </span>
          ))}

        {/* Selected single value shown as text when closed */}
        {!isMulti && !open && props.value != null && (
          <span className="truncate">{labelFor(options, props.value)}</span>
        )}

        {/* Search input */}
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            open && rows[activeIndex] ? `${listboxId}-opt-${activeIndex}` : undefined
          }
          aria-label={ariaLabel}
          disabled={disabled}
          value={query}
          placeholder={
            (isMulti ? selectedValues.length === 0 : props.value == null) && !open
              ? placeholder
              : open
                ? "Type to filter…"
                : ""
          }
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          className={
            "flex-1 min-w-[3rem] bg-transparent outline-none placeholder:text-muted-foreground " +
            (showQueryInline ? "" : "w-0")
          }
        />

        <ChevronDown
          className="w-4 h-4 text-muted-foreground shrink-0 ml-auto pointer-events-none"
          aria-hidden="true"
        />
      </div>

      {/* Dropdown listbox — portaled to <body> with position:fixed so no
          ancestor overflow clips it or stacking context buries it. */}
      {open && !disabled && menuRect && createPortal(
        <ul
          ref={menuRef}
          id={listboxId}
          role="listbox"
          aria-label={ariaLabel}
          style={{
            position: "fixed",
            left: menuRect.left,
            top: menuRect.top,
            width: menuRect.width,
          }}
          className="z-[1000] max-h-60 overflow-auto rounded-lg border border-border bg-popover shadow-lg py-1 text-sm"
        >
          {rows.length === 0 ? (
            <li className="px-3 py-2 text-muted-foreground">No matches</li>
          ) : (
            rows.map((row, i) => {
              const selected = selectedValues.includes(row.value);
              return (
                <li
                  key={row.value + (row.isCustom ? ":custom" : "")}
                  id={`${listboxId}-opt-${i}`}
                  role="option"
                  aria-selected={selected}
                  onMouseEnter={() => setActiveIndex(i)}
                  onMouseDown={(e) => {
                    // mousedown (not click) so the input doesn't blur-close first
                    e.preventDefault();
                    commit(row.value);
                  }}
                  className={
                    "flex items-center gap-2 px-3 py-1.5 cursor-pointer " +
                    (i === activeIndex ? "bg-accent " : "") +
                    (row.isCustom ? "italic text-muted-foreground " : "")
                  }
                >
                  <span className="flex-1 truncate">{row.label}</span>
                  {selected && (
                    <Check className="w-3.5 h-3.5 text-primary" aria-hidden="true" />
                  )}
                </li>
              );
            })
          )}
        </ul>,
        document.body,
      )}
    </div>
  );
}
